import { Box } from '@material-ui/core';
import { LatLng, LatLngBounds, Proj, Map, Bounds } from 'leaflet';
import 'proj4leaflet';
import React, {
	createRef,
	ReactElement,
	ReactNode,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { MapContainer } from 'react-leaflet';
import { connect } from 'react-redux';
import { AppState, MapLegendItem } from '../../../redux';
import { getMapImage } from '../../../services';
import {
	extractPrefix,
	overwriteArrowheadFn,
	overwriteCRSDistanceFn,
	overwritePopupFns
} from '../../../utils';
import {
	CustomImageOverlay,
	CustomSvgOverlay,
	DebugLogger,
	LoadingContainer,
	MapReference
} from '../../atoms';

import './map-viewer.styles.scss';

(window as any).setprojection = (val: boolean) => {
	localStorage.setItem('disableproj', `${!val}`);
};

export interface IMapViewerProps {
	mapId: string;
	prefix?: string;
	corner: LatLng;
	oppositeCorner: LatLng;
	casperId: string;
	children?: ReactNode;
	hideMap?: boolean | undefined;
	projection?: string;
}

export interface IMapViewerStateProps {
	legend?: MapLegendItem[];
}

function MapViewerView(
	props: IMapViewerProps & IMapViewerStateProps
): ReactElement {
	const {
		prefix,
		corner,
		oppositeCorner,
		casperId,
		children,
		legend,
		hideMap,
		projection
	} = props;
	const svgRef: RefObject<SVGSVGElement> = createRef<SVGSVGElement>();
	const isMountedRef = useRef<boolean>(false);
	const [mapRef, setMapRef] = useState<Map>();
	const [imageSource, setImageSource] =
		useState<{ data: string; svg: boolean } | undefined>();
	const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);
	const isProjectionUsed =
		localStorage.getItem('disableproj') === 'true' ? false : true;

	const mapBounds: LatLngBounds = useMemo(() => {
		return new LatLngBounds(corner, oppositeCorner);
	}, [corner, oppositeCorner]);

	const crs: Proj.CRS | undefined = useMemo<Proj.CRS | undefined>(() => {
		if (!projection) return undefined;
		if (projection === '' || projection.toLowerCase() === 'x')
			return undefined;
		const tmpCrs = new Proj.CRS('ESRI:102008', `${projection}`, {
			resolutions: [
				8192, 4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1,
				0.5, 0.1
			]
		});

		const crs: Proj.CRS = new Proj.CRS('ESRI:102008', `${projection}`, {
			bounds: new Bounds(
				tmpCrs.project(corner),
				tmpCrs.project(oppositeCorner)
			),
			resolutions: [
				65536, 32768, 16384, 8192, 4096, 2048, 1024, 512, 256, 128, 64,
				32, 16, 8, 4, 2, 1, 0.5, 0.1
			]
		});

		overwriteCRSDistanceFn(crs);
		overwriteArrowheadFn();
		overwritePopupFns();

		return crs;
	}, [mapRef, projection]);

	const crsProps: { crs: Proj.CRS } | {} =
		crs && isProjectionUsed ? { crs } : {};

	useEffect(() => {
		isMountedRef.current = true;
		loadImage();
		return () => {
			isMountedRef.current = false;
		};
	}, [casperId]);

	useEffect(() => {
		setLayers();
	}, [legend]);

	async function loadImage() {
		setIsLoadingImage(true);

		const image: Response = await getMapImage(casperId, prefix);
		if (isSVG(image)) {
			const imgText: string = await image.text();
			if (isMountedRef.current)
				setImageSource({ data: imgText, svg: true });
		} else {
			const blob: Blob = new Blob([await image.arrayBuffer()], {
				type: 'image/png'
			});
			const urlCreator = window.URL;
			setImageSource({
				data: urlCreator.createObjectURL(blob),
				svg: false
			});
		}

		setIsLoadingImage(false);
	}

	function isSVG(response: Response): boolean {
		const contentType: string | null = response.headers.get('content-type');
		if (contentType !== null)
			return contentType.match(/image\/svg\+xml/) !== null;
		return false;
	}

	function setLayers(): void {
		if (!legend || !isMountedRef.current) return;

		legend.forEach((item: MapLegendItem) => {
			setLayerState(item);
		});
	}

	function setLayerState(legendItem: MapLegendItem): void {
		const svg: SVGSVGElement = svgRef.current
			? svgRef.current
			: (document.getElementById(getSvgId()) as unknown as SVGSVGElement);

		if (!svg) return;

		const element = document.getElementById(
			legendItem.layerKey
		) as unknown as SVGSVGElement;

		if (element === null || !element.style) return;
		element.style.visibility = legendItem.active ? 'visible' : 'hidden';
	}

	function getSvgId() {
		if (!prefix) return `image-${extractPrefix(casperId)}`;
		return `image-${prefix}-${extractPrefix(casperId)}`;
	}

	return (
		<Box className="map-viewer">
			<LoadingContainer isLoading={isLoadingImage}>
				<MapContainer
					bounds={mapBounds}
					center={mapBounds.getCenter()}
					attributionControl={false}
					zoomControl={false}
					doubleClickZoom={false}
					fullscreenControl={true}
					zoomSnap={0.25}
					wheelPxPerZoomLevel={180}
					{...crsProps}
				>
					<MapReference onMapReference={setMapRef} />
					{!hideMap && imageSource && imageSource.svg && (
						<CustomSvgOverlay
							id={getSvgId()}
							imageText={imageSource.data}
							bounds={mapBounds}
							crs={crs}
						/>
					)}
					{!hideMap && imageSource && !imageSource.svg && (
						<CustomImageOverlay
							bounds={mapBounds}
							url={imageSource.data}
							crs={crs}
						/>
					)}
					<DebugLogger />
					{children}
				</MapContainer>
			</LoadingContainer>
		</Box>
	);
}

function mapStateToProps(state: AppState, ownProps: IMapViewerProps) {
	return {
		legend: state.legend.legends[ownProps.mapId]
	};
}

export const MapViewer = connect(mapStateToProps)(MapViewerView);
